import { chatsTypes } from "./chats.types";

export const INITIAL_STATE = {
  // keeps record of messages that are being uploaded to the server according to the conversationId
  pendingMessages: {},
  teams: {
    // teams that agent is member of it
    memberofTeams: [],
    // all teams
    allTeams: [],
  },
  selectedConversations: "pending",
  selectedConversation: null,
  unreadPendingMessage: false,
  unreadOngoingMessage: false,
  isLoadingChats: false,
  scrollToBottom: true,
  typingWord: null,
  transferedChats: [],
  pretexts: [],
  pending: {
    hasMoreToLoad: false,
    loading: true,
    loadLimit: 30,
    data: [],
    totalCount: 0,
  },
  ongoing: {
    hasMoreToLoad: false,
    loading: true,
    loadLimit: 30,
    data: [],
    totalCount: 0,
  },
  otherConvs: {
    hasMoreToLoad: false,
    loading: true,
    loadLimit: 30,
    data: [],
    totalCount: 0,
  },
  messageUnderAction: null,
  blockedUsers: [],
  maxValidCharactersCount: 1500,
};

const getUserConversationState = (state, payload) => {
  let { user, conversation } = payload;
  switch (state.selectedConversations) {
    case "pending":
      return {
        ...state,
        pending: {
          ...state.pending,
          data: {
            ...state.pending.data,
            [conversation.id]: {
              ...state.pending.data[conversation.id],
              user: {
                ...state.pending.data[conversation.id].user,
                ...user,
              },
              conversation,
            },
          },
        },
      };
    case "ongoing":
      return {
        ...state,
        ongoing: {
          ...state.ongoing,
          data: {
            ...state.ongoing.data,
            [conversation.id]: {
              ...state.ongoing.data[conversation.id],
              user: {
                ...state.ongoing.data[conversation.id].user,
                ...user,
              },
              conversation,
            },
          },
        },
      };

    default:
      break;
  }
};

const getFilteredOtherOperatorsConvsList = (prevList, convId) =>
  prevList.filter((conv) => conv.id !== convId);

const chatsReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case chatsTypes.SET_SELECTED_CONVERSATIONS:
      return {
        ...state,
        selectedConversations: action.payload,
      };

    case chatsTypes.SET_HAS_MORE_TO_LOAD: {
      const { convType, hasMoreToLoad } = action.payload;

      return {
        ...state,
        [convType]: {
          ...state[convType],
          hasMoreToLoad,
        },
      };
    }

    case chatsTypes.SET_CONVERSATIONS_LIST: {
      const { convType, data, totalCount } = action.payload;

      return {
        ...state,
        [convType]: {
          ...state[convType],
          loading: false,
          data,
          totalCount,
        },
      };
    }

    case chatsTypes.UPDATE_PENDING_CONVERSATIONS:
      let adaptedPendings = state.pending.data;
      const prevTotalCount = state.pending.totalCount;
      let conversationIndex = adaptedPendings.findIndex(
        (conversation) => conversation.id === action.payload.conversationId
      );

      const newTotalCount =
        conversationIndex >= 0 ? prevTotalCount : prevTotalCount + 1;

      // conversation was already added to the convs list
      if (conversationIndex >= 0) {
        adaptedPendings = adaptedPendings.map((conversation) => {
          // new pending message has already been in the list so merge it and increment notSeenMessages by 1
          if (conversation.id === action.payload.conversationId) {
            return {
              ...conversation,
              ...action.payload.conversation,
              notSeenMessages: conversation.notSeenMessages + 1,
            };
          } else {
            return conversation;
          }
        });
      } else {
        // if new pending is not already in the list then merge it and set notSeenMessages to 1
        adaptedPendings = [
          { ...action.payload.conversation, notSeenMessages: 1 },
          ...adaptedPendings,
        ];
      }

      return {
        ...state,
        pending: {
          ...state.pending,
          loading: false,
          data: adaptedPendings,
          totalCount: newTotalCount,
        },
      };
    case chatsTypes.UPDATE_ONGOING_CONVERSATIONS: {
      const { conversation: payloadConv, updateNotSeen } = action.payload;
      const { totalCount: prevTotalCount, data: ongoingConvsData } =
        state.ongoing;
      const convId = payloadConv.id;

      const conversation = ongoingConvsData.find(
        (conversation) => conversation?.id === convId
      );

      // conversation already exists in convs list
      if (conversation) {
        let index = ongoingConvsData.findIndex((conv) => conv?.id === convId);

        const prevNotSeenCount = conversation.notSeenMessages;
        const newNotSeenCount = payloadConv.notSeenMessages;

        const updatedNotSeenCount = !updateNotSeen
          ? prevNotSeenCount
          : newNotSeenCount || newNotSeenCount === 0
          ? newNotSeenCount
          : prevNotSeenCount + 1;

        return {
          ...state,
          ongoing: {
            ...state.ongoing,
            loading: false,
            data: [
              ...ongoingConvsData.slice(0, index),
              {
                ...ongoingConvsData[index],
                ...payloadConv,
                notSeenMessages: updatedNotSeenCount,
              },
              ...ongoingConvsData.slice(index + 1),
            ],
          },
        };
      } else
        return {
          ...state,
          ongoing: {
            ...state.ongoing,
            loading: false,
            data: [
              {
                ...payloadConv,
                notSeenMessages: 1,
              },
              ...state.ongoing.data,
            ],
            totalCount: prevTotalCount + 1,
          },
        };
    }

    case chatsTypes.UPDATE_CONVERSATION_AVATAR:
      return {
        ...state,
        [action.payload.conversationType]: {
          ...state[action.payload.conversationType],
          data: state[action.payload.conversationType].data.map(
            (conversation) => {
              const convObj = { ...conversation };
              if (conversation.id === action.payload.conversationId) {
                if (action.payload.avatar !== undefined)
                  convObj.user["avatar"] = action.payload.avatar;
                if (action.payload.progress !== undefined)
                  convObj.user["progress"] = action.payload.progress;
              }
              return convObj;
            }
          ),
        },
      };

    case chatsTypes.SET_SELECTED_CONVERSATION: {
      const data = action.payload;

      const { conversation } = data || {};
      const conversationId = conversation?.id;
      const convType = conversation?.convType;

      const selectedConversations = ["closed", "otherConvs"].includes(convType)
        ? "otherConvs"
        : convType
        ? convType
        : state.selectedConversations;

      const pendingMessagesForConvId =
        state.pendingMessages[conversationId] || [];

      const allMessageIds = conversation?.chats.map((item) => item.id);

      // prevents re-appending messages that are appended once before. this corner case happens when user scrolls up and fetch more is fired
      const pendingMessageAreNotAlreadyAppended = allMessageIds
        ? pendingMessagesForConvId.filter(
            (item) => !allMessageIds.includes(item.id)
          )
        : [];

      const updatedSelectedConversation = data
        ? {
            ...data,
            conversation: {
              ...conversation,
              convType,
              chats: [
                ...conversation.chats,
                ...pendingMessageAreNotAlreadyAppended,
              ],
            },
          }
        : data;

      return {
        ...state,
        selectedConversations,
        selectedConversation: updatedSelectedConversation,
      };
    }

    case chatsTypes.SET_USER_CONVERSATION_INFO:
      return getUserConversationState(state, action.payload);
    case chatsTypes.SET_NEW_MESSAGE:
      let selectedConversationId =
        state.selectedConversation &&
        state.selectedConversation.conversation &&
        state.selectedConversation.conversation.id;
      if (selectedConversationId === action.payload.conversationId) {
        return {
          ...state,
          scrollToBottom: true,
          typingWord: null,
          selectedConversation: {
            ...state.selectedConversation,
            conversation: {
              ...state.selectedConversation?.conversation,
              chats: [
                ...(state.selectedConversation?.conversation?.chats || []),
                action.payload.message,
              ],
            },
          },
        };
      } else {
        return state;
      }
    case chatsTypes.REMOVE_CONVERSATION_NOT_SEEN_BADGE:
      const selectedConvs = state.selectedConversations;
      let convs = state[selectedConvs].data.map((conversation) => {
        if (conversation?.id === action.payload.conversationId) {
          return {
            ...conversation,
            notSeenMessages: 0,
          };
        } else {
          return conversation;
        }
      });
      return {
        ...state,
        [selectedConvs]: {
          ...state[selectedConvs],
          data: convs,
        },
      };
    case chatsTypes.SET_DELIVERED_MESSAGE:
      let adaptedChats = state.selectedConversation.conversation.chats.map(
        (chat) => {
          if (chat?.id && chat.id === action.payload) {
            return {
              ...chat,
              delivered: true,
            };
          } else {
            return chat;
          }
        }
      );
      return {
        ...state,
        selectedConversation: {
          ...state.selectedConversation,
          conversation: {
            ...state.selectedConversation.conversation,
            chats: adaptedChats,
          },
        },
      };
    case chatsTypes.ADD_CONVERSATION_TO_ONGOINGS: {
      const pendingData = state.pending.data;

      const acceptedConversation = pendingData.find(
        (conv) => conv.id === action.payload
      );

      let pendingConversations = pendingData.filter(
        (conv) => conv.id !== action.payload
      );

      const prevOngoingConvsTotalCount = state.ongoing.totalCount;

      return {
        ...state,
        pending: {
          ...state.pending,
          data: pendingConversations,
        },
        ongoing: {
          ...state.ongoing,
          data: [
            { ...acceptedConversation, inChatWithBot: false },
            ...state.ongoing.data,
          ],
          totalCount: prevOngoingConvsTotalCount + 1,
        },
      };
    }

    /**
     * this approach is not correct because it does not consider conversations that are no listed. but It is a better approach than previous one.
     * TODO: to correctly fix it sync with backend to get conversation status too.
     */

    case chatsTypes.REMOVE_CLOSED_CONVERSATION: {
      const convId = action.payload;

      const convType = ["pending", "ongoing", "otherConvs"].find((convType) => {
        return state[convType].data.some((c) => c.id === convId);
      });

      if (!convType) return state;

      const updatedConvs = state[convType].data.filter((c) => c.id !== convId);
      const prevTotals = state[convType].totalCount;
      const updatedSelectedConv =
        state.selectedConversation?.conversation?.id === convId
          ? null
          : state.selectedConversation;
      const newTotalCount = prevTotals - 1;

      return {
        ...state,
        selectedConversation: updatedSelectedConv,
        [convType]: {
          ...state[convType],
          data: updatedConvs,
          totalCount: newTotalCount,
        },
      };
    }

    case chatsTypes.SET_TRANSFERED_CHAT:
      return {
        ...state,
        transferedChats: [...state.transferedChats, action.payload],
      };

    case chatsTypes.REMOVE_CONV_FROM_LIST: {
      const { conversationId, listType } = action.payload;
      const prevTotalCount = state[listType].totalCount;

      // remove closed conversation from otherOperators conversations list
      const filteredOtherOperatorsConvs = getFilteredOtherOperatorsConvsList(
        state[listType].data,
        conversationId
      );

      return {
        ...state,
        [listType]: {
          ...state[listType],
          data: filteredOtherOperatorsConvs,
          totalCount: prevTotalCount - 1,
        },
      };
    }

    case chatsTypes.REMOVE_TRANSFERRED_CHAT: {
      const adaptedTransferedChats = state.transferedChats.filter(
        (chat) => chat.conversationId !== action.payload
      );

      return {
        ...state,
        transferedChats: adaptedTransferedChats,
      };
    }

    // Adds newly transferred conversation object to the list
    case chatsTypes.SET_TRANSFERED_CHAT_CONVERSATION: {
      const prevOngoingConvsTotalCount = state.ongoing.totalCount;

      return {
        ...state,
        ongoing: {
          ...state.ongoing,
          data: [action.payload, ...state.ongoing.data],
          totalCount: prevOngoingConvsTotalCount + 1,
        },
      };
    }

    case chatsTypes.SET_PRETEXTS:
      return {
        ...state,
        pretexts: action.payload,
      };
    case chatsTypes.SET_UPDATED_USER:
      if (
        state.selectedConversation &&
        state.selectedConversation.user &&
        state.selectedConversation.user.id === action.payload.id
      ) {
        return {
          ...state,
          selectedConversation: {
            ...state.selectedConversation,
            user: {
              ...state.selectedConversation.user,
              name: action.payload.name,
              phone: action.payload.phone,
              email: action.payload.email,
              about: action.payload.about,
            },
          },
        };
      } else {
        return state;
      }

    case chatsTypes.UPDATE_CHAT_FILE_UPLOAD_PROGRESS: {
      const clonedSelectedConv = [
        ...state.selectedConversation.conversation.chats,
      ];

      const uploadingFileMessageIndex = clonedSelectedConv.findIndex(
        (chat) => chat.id === action.payload.messageId
      );

      if (uploadingFileMessageIndex !== -1) {
        const targetedChat = clonedSelectedConv[uploadingFileMessageIndex];

        clonedSelectedConv[uploadingFileMessageIndex] = {
          ...targetedChat,
          progress: action.payload.progress,
        };
      }

      return {
        ...state,
        selectedConversation: {
          ...state.selectedConversation,
          conversation: {
            ...state.selectedConversation.conversation,
            chats: clonedSelectedConv,
          },
        },
      };
    }

    case chatsTypes.SET_USER_AVATAR_UPLOAD_PROGRESS:
      if (
        state.selectedConversation &&
        state.selectedConversation.user &&
        state.selectedConversation.user.id === action.payload.id
      ) {
        return {
          ...state,
          selectedConversation: {
            ...state.selectedConversation,
            user: {
              ...state.selectedConversation.user,
              avatarProgress: action.payload.progress,
            },
          },
        };
      }

      return state;

    case chatsTypes.SET_UNREAD_ONGOING_MESSAGE:
      return {
        ...state,
        unreadOngoingMessage: action.payload,
      };
    case chatsTypes.SET_UNREAD_PENDING_MESSAGE:
      return {
        ...state,
        unreadPendingMessage: action.payload,
      };
    case chatsTypes.SET_SCROLL_TO_BOTTOM:
      return {
        ...state,
        scrollToBottom: action.payload,
      };
    case chatsTypes.SET_LOADING_CHATS:
      return {
        ...state,
        isLoadingChats: action.payload,
      };

    case chatsTypes.SET_TYPING_WORD:
      const selected = state.selectedConversation;
      let word =
        selected && selected.conversation.id === action.payload.conversationId
          ? action.payload.msg
          : null;
      return {
        ...state,
        typingWord: word,
      };

    case chatsTypes.SET_ACTING_MESSAGE:
      return {
        ...state,
        messageUnderAction: action.payload,
      };

    case chatsTypes.SET_EDITED_MESSAGE:
      const editedChats = state.selectedConversation.conversation.chats.map(
        (chat) => {
          if (chat.id && chat.id === action.payload.msgId) {
            return {
              ...chat,
              content: action.payload.msgContent,
              edited: true,
            };
          }
          return chat;
        }
      );

      return {
        ...state,
        selectedConversation: {
          ...state.selectedConversation,
          conversation: {
            ...state.selectedConversation.conversation,
            chats: editedChats,
          },
        },
      };

    case chatsTypes.LOGOUT_USER:
      return INITIAL_STATE;

    case chatsTypes.SET_SEEN_STATUS_FOR_ALL_MESSAGES:
      if (state.selectedConversation) {
        let currentConvChats = [
          ...(state.selectedConversation?.conversation?.chats || []),
        ];

        if (
          state.selectedConversation?.conversation?.id ===
          action.payload.conversationId
        ) {
          currentConvChats = (
            state.selectedConversation?.conversation?.chats || []
          ).map((chat) => {
            if (chat.me) chat.status = "seen";
            return chat;
          });
        }

        return {
          ...state,
          selectedConversation: {
            ...state.selectedConversation,
            conversation: {
              ...state.selectedConversation?.conversation,
              chats: currentConvChats,
            },
          },
        };
      }

      return state;

    case chatsTypes.SET_MESSAGE_ID:
      const correctedChats = state.selectedConversation.conversation.chats.map(
        (chat) => {
          if (chat.id === action.payload.prevId) chat.id = action.payload.newId;
          return chat;
        }
      );

      return {
        ...state,
        selectedConversation: {
          ...state.selectedConversation,
          conversation: {
            ...state.selectedConversation.conversation,
            chats: correctedChats,
          },
        },
      };

    // adds a chat message that being uploaded to the pending messages list
    case chatsTypes.ADD_PENDING_MESSAGE_TO_THE_LIST: {
      const conversationId = action.payload.conversationId;
      const pendingMessage = { ...action.payload.message };

      const pendingMessegesForConvId =
        state.pendingMessages[conversationId] || [];

      const updatedPendingMessagesForConvId = [
        ...pendingMessegesForConvId,
        pendingMessage,
      ];

      return {
        ...state,
        pendingMessages: {
          ...state.pendingMessages,
          [conversationId]: updatedPendingMessagesForConvId,
        },
      };
    }

    // removes a pending message from pending messages list when it is uploaded completely
    case chatsTypes.REMOVE_MESSAGE_FROM_PENDING_LIST: {
      const { conversationId, messageId } = action.payload;
      const pendingMessegesForConvId =
        state.pendingMessages[conversationId] || [];

      const updatedPendingMessagesForConvId = pendingMessegesForConvId.filter(
        (item) => item.id !== messageId
      );

      return {
        ...state,
        pendingMessages: {
          ...state.pendingMessages,
          [conversationId]: updatedPendingMessagesForConvId,
        },
      };
    }

    case chatsTypes.SET_BLOCKED_USERS_LIST: {
      return {
        ...state,
        blockedUsers: action.payload,
      };
    }

    case chatsTypes.SET_TEAMS: {
      return {
        ...state,
        teams: action.payload,
      };
    }

    case chatsTypes.SET_CONVERSATION_WITH_BOT_STATUS: {
      const updatedPendingConvs = state.pending.data.map((item) => {
        if (item.id === action.payload.conversationId) {
          return { ...item, inChatWithBot: action.payload.status };
        }

        return item;
      });

      return {
        ...state,
        pending: {
          ...state.pending,
          data: updatedPendingConvs,
        },
      };
    }

    // set conversation as `inChatWithBot` type and also set last message
    case chatsTypes.UPDATE_PENDING_CONVERSATION_BY_BOT_NEW_MSG: {
      const updatedPendingConvs = state.pending.data.map((item) => {
        if (item.id === action.payload.conversationId) {
          return {
            ...item,
            inChatWithBot: true,
            lastMessage: action.payload.message,
          };
        }

        return item;
      });

      return {
        ...state,
        pending: {
          ...state.pending,
          data: updatedPendingConvs,
        },
      };
    }

    default:
      return state;
  }
};

export default chatsReducer;
